import { useState, useCallback } from "react";
import { axiosInstance } from "../../../utils/axios";

export const useResumeSearch = (config = {}) => {
  const { hiddenFilters = null } = config;

  const [searchCache, setSearchCache] = useState({});
  const [searchingFile, setSearchingFile] = useState(null);

  // Store filter state per file
  const [fileFilterState, setFileFilterState] = useState({});

  const executeSearch = useCallback(
    async (fileId, keywords, options = {}) => {
      const { page = 1, filters = {}, sortBy = "", queryUUID = null, groupBy = [] } = options;

      if (!fileId || !keywords || keywords.length === 0) return null;
      if (searchingFile === fileId) return null;

      const searchPhrase = keywords.join(", ");
      setSearchingFile(fileId);

      // Save filter state for this file (without hiddenFilters - only user-selected filters)
      setFileFilterState((prev) => ({
        ...prev,
        [fileId]: {
          filterOptions: filters,
          selectedSort: sortBy,
          groupBy: groupBy ?? prev[fileId]?.groupBy ?? [],
        },
      }));

      try {
        // Merge hiddenFilters only for API call, not for storage
        let apiFilters = filters;

        if (hiddenFilters) {
          apiFilters = {
            include_filters: {
              ...(filters.include_filters || {}),
              ...(hiddenFilters.include_filters || {}),
            },
            exclude_filters: {
              ...(filters.exclude_filters || {}),
              ...(hiddenFilters.exclude_filters || {}),
            },
          };
        }

        const payload = {
          search_phrase: searchPhrase,
          filters: apiFilters,
          sort_by: sortBy,
          group_by: groupBy,
        };

        if (queryUUID) {
          payload.query_uuid = queryUUID;
          payload.page_no = page;
        }

        const response = await axiosInstance.post("/search", payload);
        const data = response.data.data;

        // Merge facets into object format
        const mergedFilters = {};
        data.facets?.forEach((facet) => {
          const key = Object.keys(facet)[0];
          mergedFilters[key] = facet[key];
        });

        const cacheData = {
          searchPhrase,
          keywords: [...keywords],
          results: data.results || [],
          filters: mergedFilters,
          total: data.total || 0,
          pagination: data.pagination || null,
          queryUUID: data.query_uuid || null,
          resultsFields: data.results_fields || {},
          searchedAt: new Date().toISOString(),
          error: null,
          currentPage: page,
          isGrouped: data.group_by ?? false,
        };

        setSearchCache((prev) => ({
          ...prev,
          [fileId]: cacheData,
        }));

        setSearchingFile(null);
        return cacheData;
      } catch (error) {
        console.error("Search error:", error);

        const errorData = {
          searchPhrase,
          keywords: [...keywords],
          results: [],
          filters: {},
          total: 0,
          pagination: null,
          queryUUID: null,
          error: error.response?.data?.message || error.message || "Search failed",
          searchedAt: new Date().toISOString(),
          currentPage: 1,
          isGrouped: false,
        };

        setSearchCache((prev) => ({
          ...prev,
          [fileId]: errorData,
        }));

        setSearchingFile(null);
        return errorData;
      }
    },
    [searchingFile, hiddenFilters],
  );

  const getCache = useCallback(
    (fileId) => {
      return searchCache[fileId] || null;
    },
    [searchCache],
  );

  const hasCache = useCallback(
    (fileId) => {
      const cache = searchCache[fileId];
      return !!(cache && cache.results && !cache.error);
    },
    [searchCache],
  );

  const clearCache = useCallback((fileId) => {
    if (fileId) {
      setSearchCache((prev) => {
        const { [fileId]: _, ...rest } = prev;
        return rest;
      });
      setFileFilterState((prev) => {
        const { [fileId]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setSearchCache({});
      setFileFilterState({});
    }
  }, []);

  const getFileFilterState = useCallback(
    (fileId) => {
      return (
        fileFilterState[fileId] || {
          filterOptions: {},
          selectedSort: "",
          groupBy: [],
        }
      );
    },
    [fileFilterState],
  );

  const setFileFilters = useCallback((fileId, filterOptions, selectedSort, groupBy) => {
    setFileFilterState((prev) => ({
      ...prev,
      [fileId]: {
        filterOptions: filterOptions ?? prev[fileId]?.filterOptions ?? {},
        selectedSort: selectedSort ?? prev[fileId]?.selectedSort ?? "",
        groupBy: groupBy ?? prev[fileId]?.groupBy ?? [],
      },
    }));
  }, []);

  return {
    searchCache,
    searchingFile,
    fileFilterState,
    executeSearch,
    getCache,
    hasCache,
    clearCache,
    getFileFilterState,
    setFileFilters,
  };
};